import { supabase } from './supabase'
import { handleApiError } from '@/utils/error-handlers'
import type { RecurrencePattern } from '@/types/event-types'

// Need to import this type but avoid circular dependency
// events.ts will import from this file, so we can't import directly from events.ts
interface EventInsertType {
  id?: string
  start_time?: string
  end_time?: string
  is_recurring?: boolean
  recurrence_pattern?: RecurrencePattern | null
  recurrence_count?: number | null
  recurrence_end_date?: string | null
  [key: string]: any // Allow for other fields
}

/**
 * Creates multiple events based on recurrence pattern
 * @param baseEventData - The base event data to create recurring instances from
 * @returns Object containing the first created event data, error status, and count of recurring events
 */
export async function createRecurringEvents(baseEventData: EventInsertType) {
  try {
    const { recurrence_pattern, recurrence_count, recurrence_end_date, start_time, end_time } =
      baseEventData

    if (!start_time || !end_time) {
      throw new Error('Start time and end time are required for recurring events')
    }

    // Calculate event duration in milliseconds
    const startDate = new Date(start_time)
    const endDate = new Date(end_time)
    const durationMs = endDate.getTime() - startDate.getTime()

    // Determine how many events to create
    const occurrences = calculateOccurrences(
      startDate,
      recurrence_pattern as RecurrencePattern,
      recurrence_count,
      recurrence_end_date
    )

    // Generate event dates
    const eventDates = generateEventDates(
      startDate,
      durationMs,
      recurrence_pattern as RecurrencePattern,
      occurrences,
      recurrence_end_date
    )

    // Create all events
    const events = eventDates.map((dates, index) => {
      // Create a copy of the base event data
      const eventCopy = { ...baseEventData }

      // Set the specific dates for this occurrence
      eventCopy.start_time = dates.start.toISOString()
      eventCopy.end_time = dates.end.toISOString()

      // Only the first event should have recurrence info
      if (index > 0) {
        eventCopy.is_recurring = false
        eventCopy.recurrence_pattern = null
        eventCopy.recurrence_count = null
        eventCopy.recurrence_end_date = null
      }

      return eventCopy
    })

    // Insert all events
    const { data, error } = await supabase.from('events').insert(events).select()

    if (error) throw new Error(`Failed to create recurring events: ${error.message}`)

    // Return the first event as the result
    return {
      data: data && data.length > 0 ? data[0] : null,
      error: null,
      recurringCount: data?.length || 0,
    }
  } catch (error) {
    return handleApiError(error, 'creating recurring events')
  }
}

/**
 * Calculates how many occurrences to create based on pattern and end date
 * @param startDate - The starting date of the series
 * @param recurrencePattern - The pattern of recurrence (daily, weekly, etc.)
 * @param recurrenceCount - Number of occurrences specified by user (optional)
 * @param recurrenceEndDate - End date for the series (optional)
 * @returns Number of occurrences to create
 */
function calculateOccurrences(
  startDate: Date,
  recurrencePattern?: RecurrencePattern,
  recurrenceCount?: number | null,
  recurrenceEndDate?: string | null
): number {
  let occurrences = recurrenceCount || 10 // Default to 10 if not specified

  // If end date is specified, calculate occurrences based on that
  if (recurrenceEndDate) {
    const endRecurrenceDate = new Date(recurrenceEndDate)
    let maxOccurrences = 100 // Safety limit

    switch (recurrencePattern) {
      case 'daily':
        maxOccurrences =
          Math.floor((endRecurrenceDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)) +
          1
        break
      case 'weekly':
        maxOccurrences =
          Math.floor(
            (endRecurrenceDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
          ) + 1
        break
      case 'biweekly':
        maxOccurrences =
          Math.floor(
            (endRecurrenceDate.getTime() - startDate.getTime()) / (14 * 24 * 60 * 60 * 1000)
          ) + 1
        break
      case 'monthly':
        // Approximate - doesn't account for varying month lengths
        maxOccurrences =
          Math.floor(
            (endRecurrenceDate.getTime() - startDate.getTime()) / (30 * 24 * 60 * 60 * 1000)
          ) + 1
        break
    }

    // Use the smaller of the two values
    occurrences = Math.min(occurrences, maxOccurrences)
  }

  // Ensure at least one occurrence
  return Math.max(1, occurrences)
}

/**
 * Generates the dates for all occurrences in the series
 * @param startDate - The starting date of the series
 * @param durationMs - Duration of each event in milliseconds
 * @param recurrencePattern - The pattern of recurrence (daily, weekly, etc.)
 * @param occurrences - Number of occurrences to generate
 * @param recurrenceEndDate - End date to limit generation (optional)
 * @returns Array of date pairs (start and end) for each occurrence
 */
function generateEventDates(
  startDate: Date,
  durationMs: number,
  recurrencePattern?: RecurrencePattern,
  occurrences: number = 10,
  recurrenceEndDate?: string | null
): { start: Date; end: Date }[] {
  const eventDates: { start: Date; end: Date }[] = []

  for (let i = 0; i < occurrences; i++) {
    const newStartDate = new Date(startDate)

    // Calculate the date for this occurrence
    switch (recurrencePattern) {
      case 'daily':
        newStartDate.setDate(newStartDate.getDate() + i)
        break
      case 'weekly':
        newStartDate.setDate(newStartDate.getDate() + i * 7)
        break
      case 'biweekly':
        newStartDate.setDate(newStartDate.getDate() + i * 14)
        break
      case 'monthly':
        newStartDate.setMonth(newStartDate.getMonth() + i)
        break
      default:
        // If no pattern specified, don't modify the date (just create one event)
        if (i > 0) continue
    }

    // Skip if beyond end date
    if (recurrenceEndDate && newStartDate > new Date(recurrenceEndDate)) {
      break
    }

    const newEndDate = new Date(newStartDate.getTime() + durationMs)

    eventDates.push({
      start: newStartDate,
      end: newEndDate,
    })
  }

  // Ensure at least one event is created
  if (eventDates.length === 0) {
    eventDates.push({
      start: startDate,
      end: new Date(startDate.getTime() + durationMs),
    })
  }

  return eventDates
}
